import React, { useEffect, useState } from "react";
import {
  Card,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PrimaryButton } from "../../components/ui/Button";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../../routes/routerConfig";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/auth/eye-off.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/auth/eye.svg";
import {
  authSelector,
  userLoanCatSelector,
} from "../../services/redux/slices/authSlice";
import ProfileAvatar from "../../components/common/ProfileAvatar";
import ProfilePicUploadPopup from "../../components/common/ProfilePicUploadPopup";
import { decrypt, encrypt } from "../../utils/crypto";
import { InputField } from "../../components/ui/Inputs";
import { Box } from "@mui/system";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import userProfileServices from "../../services/api/service/userProfileService";
import regex from "../../data/regex";
import Skeleton from "@mui/material/Skeleton";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { trackEvent } from "../../utils/ga";
import ConfirmDialog from "../../components/ui/ConfirmModalDelete";

interface FormInputs {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}
interface Payload {
  cif: any;
  businessId: any;
  loanCategoryId: any;
}
const UserProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userProfile, businessType } = useSelector(authSelector);
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(false);
  const [profileDetails, setProfileDetails] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    address: "",
    state: "",
    city: "",
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    reset,
  } = useForm<FormInputs>();

  const oldPassword = watch("oldPassword");
  const newPassword = watch("newPassword");
  const userLoanCategory = useSelector(userLoanCatSelector);

  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { loanCategory } = useSelector(authSelector);
  const { Flexi, TermLoan } = loanCategory;

  const handleToastOpen = (
    message: string,
    severity: "error" | "warning" | "info" | "success"
  ) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  });
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
     console.log(userProfile,"userProfile")
      try {
        const payload: Payload = {
          cif: userProfile?.cifNumber?userProfile?.cifNumber:userProfile?.phoneNumber,
          businessId: encrypt(businessType?.id),
          loanCategoryId: encrypt(
            userLoanCategory?.TermLoan[0]?.loanCategoryId
          ),
        };
        const response = await userProfileServices.getProfileDetails(payload);
        setProfileDetails({
          name: response.data.data.name || "", // Provide default values if needed
          email: response.data.data.email || "",
          phoneNumber: response.data.data.phoneNumber || "",
          address: response.data.data.address || "",
          state: response.data.data.state || "",
          city: response.data.data.city || "",
        });
      } catch (error) {
        console.error("Error fetching profile details:", error);
        // Handle error, e.g., show error message
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  const handleChangePassword = async (data: any) => {
    try {
      const res = await userProfileServices.changeUserPassword(
        userProfile?.id,
        data
      );

      if (res.status == 200) {
        reset();
        dispatch(
          setStatusPopup({
            status: "success",
            message: "Profile Password Updated successfully",
            locationPath: "/profile",
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
          locationPath: "/profile",
        })
      );
    }
  };

  const handleDeleteUser = async () => {
    try {

      const res = await userProfileServices.deactivateUser();
      if (res.status === 200) {
        localStorage.clear();
        sessionStorage.clear();
        dispatch(
          setStatusPopup({
            status: "success",
            message: "Your account has been deleted successfully",
            locationPath: "/login",
          })
        );
        // Optional: You might want to logout the user and redirect to 
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message || "Failed to deactivate user account",
        })
      );
    }
  };

  const handleDeleteUserConfirm = () => {
    setConfirmModal(true);
  };

  const handleAgreeSubmit = (response: string) => {
    setConfirmModal(false);
    if (response === "Yes") {
      trackEvent('UserProfile_Delete_User', decrypt(userProfile?.id), {
        label: 'UserProfile_Delete_User',
        category: 'UserProfile_Delete_User',
        action: "UserProfile_Delete_User",
      });
      handleDeleteUser();
    }
  };

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    trackEvent("UserProfile_Change_Password", decrypt(userProfile?.id), {
      label: "UserProfile_Change_Password",
      category: "UserProfile_Change_Password",
      action: "UserProfile_Change_Password",
    });

    // Logic to handle password change goes here
    const encryptedData = {
      oldPassword: encrypt(data.oldPassword),
      newPassword: encrypt(data.newPassword),
      confirmPassword: encrypt(data.confirmPassword),
      id: userProfile?.id,
    };
    handleChangePassword(encryptedData);
  };
  const handleClick = () => {
    navigate(ROUTES?.Profile?.changePassword?.locationPath);
  };
  let breadcrumbItems = [
    { label: "Dashboard", path: (Flexi?.length > 0 || TermLoan?.length > 0) ? "/"  : "/application/existing-application" },
    { label: "User Profile" }, // Current page, no `path` needed
  ];
  if (isLoading) {
    return (
      <div className="user-profile-container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width={200} height={30} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="circular" width={104} height={104} />
            <Skeleton variant="rectangular" width={150} height={30} />
            <Skeleton variant="rectangular" width={200} height={20} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Card className="dashboard-container">
              <Grid container alignItems="center">
                <Grid item xs={12} sm={10}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "36px",
                    }}
                  >
                    <Skeleton variant="circular" width={72} height={72} />
                    <div>
                      <Skeleton variant="rectangular" width={150} height={20} />
                      <Skeleton variant="rectangular" width={200} height={15} />
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Skeleton variant="rectangular" width={120} height={35} />
                </Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2} sx={{ padding: "16px" }}>
                {[...Array(6)].map((_, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Skeleton variant="rectangular" width="100%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={40} />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width={200} height={30} />
            <Divider />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" width="100%" height={20} />
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" width="100%" height={20} />
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" width="100%" height={20} />
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width={300} height={20} />
            <Skeleton variant="rectangular" width={300} height={20} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" width={150} height={40} />
          </Grid>
        </Grid>
      </div>
    );
  }
  return (
    <Box>
      {!isXsOrSm && (
        <Box sx={{ padding: "0px 0px 18px 0px" }}>
          <Breadcrumbs items={breadcrumbItems} />
        </Box>
      )}

      <div className="user-profile-container">
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                color: "#168794",
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "20px",
              }}
            >
              Update Profile
            </Typography>
          </Grid>
        </Grid>

        <Card>
          <Grid
            container
            alignItems="center"
            className="dashboard-container"
            sx={{ width: "100%", paddingRight: "16px" }}
          >
            {/* Profile Picture and Name */}
            <Grid item xs={12} sm={10} className="form-container">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  paddingLeft: "16px",
                }}
              >
                <Box className="profile-pic-container">
                  <Box className="picture-wrapper">
                    <ProfileAvatar
                      name={userProfile?.name}
                      src={userProfile?.profileImage}
                      size={72}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    className="user-Profile-lable"
                    style={{ marginBottom: "9px", cursor: "pointer" }}
                    onClick={() => setShowUpload((prev) => !prev)}
                  >
                    Upload new photo
                  </Typography>
                  <Typography className="insidePageLableProfile">
                    Image can be JPEG, JPG, PNG and cannot exceed 10MB.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Divider />
          <div
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              marginTop: "20px",
            }}
          >
            <Grid container item spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"Name"}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder="Enter your full name"
                  value={userProfile?.fullName}
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"Email"}{" "}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={profileDetails?.email}
                  disabled
                  placeholder="Enter your email"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"Phone Number"}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled
                  type="number"
                  inputMode="decimal"
                  value={profileDetails?.phoneNumber}
                  placeholder="Enter your phone number"
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"Address"}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder="Enter your address"
                  value={profileDetails?.address}
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"State"}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  placeholder="Enter your State"
                  value={profileDetails?.state}
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <div style={{ marginBottom: "2px" }}>
                  <label
                    htmlFor="custom-autocomplete"
                    style={{
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "rgba(75, 70, 92, 0.8)",
                      lineHeight: "18px",
                    }}
                  >
                    {"City"}
                  </label>
                </div>

                <InputField
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={profileDetails?.city}
                  placeholder="Enter your City"
                  disabled
                  sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#000000",
                      color: "#000000",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </Card>

        {/* Change Password Section */}
        <Card style={{ marginTop: "20px", padding: "16px" }}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: "#4B465C",
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Change Password
            </Typography>
          </Grid>
          <Grid container alignItems="center">
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              {/* Old Password Field */}
              <Grid container>
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <div style={{ marginBottom: "2px" }}>
                    <label
                      htmlFor="custom-autocomplete"
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                        color: "rgba(75, 70, 92, 0.8)",
                        lineHeight: "18px",
                      }}
                    >
                      {"Enter Old Password"}
                    </label>
                  </div>
                  <InputField
                    type={showOldPassword ? "text" : "password"}
                    style={{ paddingRight: isXsOrSm ? "0px" : "16px" }}
                    fullWidth
                    {...register("oldPassword", {
                      required: "Old password is required",
                      minLength: {
                        value: 8,
                        message:
                          "Old Password must be at least 8 characters long",
                      },
                      maxLength: {
                        value: 15,
                        message:
                          "Old Password must be at most 15 characters long",
                      },
                      pattern: {
                        value: regex?.password,
                        message:
                          "Old Password must contain one capital letter and symbol @$!%*?& or numbers",
                      },
                    })}
                    error={!!errors.oldPassword}
                    helperText={errors?.oldPassword?.message}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowOldPassword(!showOldPassword)}
                          >
                            {" "}
                            {showOldPassword ? <EyeIcon /> : <EyeOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                {/* New Password Field */}
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <div style={{ marginBottom: "2px" }}>
                    <label
                      htmlFor="custom-autocomplete"
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                        color: "rgba(75, 70, 92, 0.8)",
                        lineHeight: "18px",
                      }}
                    >
                      {"New Password"}
                    </label>
                  </div>
                  <InputField
                    type={showNewPassword ? "text" : "password"}
                    fullWidth
                    style={{ paddingRight: isXsOrSm ? "0px" : "16px" }}
                    {...register("newPassword", {
                      required: "New password is required",
                      minLength: {
                        value: 8,
                        message: "Password must be at least 8 characters long",
                      },
                      maxLength: {
                        value: 15,
                        message: "Password must be at most 15 characters long",
                      },
                      pattern: {
                        value: regex?.password,
                        message:
                          "Password must contain one capital letter and symbol @$!%*?& or numbers",
                      },
                      validate: (value: any) =>
                        value !== oldPassword ||
                        "New password can not be same as old password  ",
                    })}
                    error={!!errors.newPassword}
                    helperText={errors?.newPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {" "}
                            {showNewPassword ? <EyeIcon /> : <EyeOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                {/* Confirm New Password Field */}
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <div style={{ marginBottom: "2px" }}>
                    <label
                      htmlFor="custom-autocomplete"
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                        color: "rgba(75, 70, 92, 0.8)",
                        lineHeight: "18px",
                      }}
                    >
                      {"Confirm New Password"}
                    </label>
                  </div>
                  <InputField
                    type={showConfirmPassword ? "text" : "password"}
                    fullWidth
                    {...register("confirmPassword", {
                      required: "Confirm password is required",
                      validate: (value: any) =>
                        value === newPassword || "Passwords do not match",
                    })}
                    error={!!errors.confirmPassword}
                    helperText={errors?.confirmPassword?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? <EyeIcon /> : <EyeOffIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    onBlur={() => {
                      if (newPassword && errors.confirmPassword) {
                        errors.confirmPassword.message =
                          "Passwords do not match";
                      }
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>
              {/* Submit Button */}
              <Grid
                item
                xs={12}
                sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  flexDirection: isXsOrSm ? "column" : "row",
                  gap: isXsOrSm ? 2 : 0
                }}
              >
                <PrimaryButton 
                  type="submit"
                  sx={{
                    width: isXsOrSm ? "100%" : "auto"
                  }}
                >
                  Update Password
                </PrimaryButton>
              </Grid>
              {/* Validation Messages */}
              <Box
                component="ul"
                sx={{ mt: 2, mb: 2, pl: 2 }}
                className="validation-messages"
              >
                <Typography component="li">
                  Between 8-15 characters without spacing
                </Typography>
                <Typography component="li">
                  Contains a combination of uppercase letters, lowercase
                  letters, numbers, and special characters
                </Typography>
              </Box>
            </form>
          </Grid>
        </Card>
      </div>
      {showUpload && (
        <ProfilePicUploadPopup
          open={showUpload}
          handleClose={() => setShowUpload((prev) => !prev)}
        />
      )}
      <ConfirmDialog
        type="warning"
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        title="Delete User Account?"
        content="Do you want to continue with your action?"
        onAgree={() => handleAgreeSubmit("Yes")}
        onDisagree={() => handleAgreeSubmit("No")}
      />
    </Box>
  );
};

export default UserProfile;
